import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSave, FiArrowLeft, FiInfo } from 'react-icons/fi';
import { exams as examsApi } from '../../api';
import toast from 'react-hot-toast';

const Field = ({ label, error, children, hint }) => (
  <div>
    <label className="label">{label}</label>
    {children}
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const Toggle = ({ label, desc, checked, onChange }) => (
  <label className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 cursor-pointer group">
    <div className="relative mt-0.5 flex-shrink-0">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="sr-only" />
      <div className={`w-10 h-5 rounded-full transition-colors ${checked ? 'bg-navy' : 'bg-gray-200'}`}>
        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </div>
    </div>
    <div>
      <p className="text-sm font-semibold text-gray-800">{label}</p>
      {desc && <p className="text-xs text-gray-400 mt-0.5">{desc}</p>}
    </div>
  </label>
);

export default function ExamCreatePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    title: '', description: '', instructions: '', category: '',
    duration_minutes: 60, total_questions: 30, passing_marks: 60,
    max_marks: 100, marks_per_question: 1, negative_marks: 0,
    start_time: '', end_time: '', max_attempts: 1,
    is_randomized: true, randomize_options: false,
    allow_review: true, show_result_immediately: true,
    display_mode: 'one_per_page', proctoring_enabled: false,
  });

  const set = (key, val) => { setForm(f => ({ ...f, [key]: val })); setErrors(e => ({ ...e, [key]: null })); };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Title is required';
    if (form.duration_minutes < 1) e.duration_minutes = 'Must be at least 1 minute';
    if (form.total_questions < 1) e.total_questions = 'Must be at least 1 question';
    if (form.passing_marks < 0 || form.passing_marks > 100) e.passing_marks = 'Must be 0–100';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.start_time) delete payload.start_time;
      if (!payload.end_time) delete payload.end_time;
      const res = await examsApi.create(payload);
      toast.success('Exam created successfully!');
      navigate(`/admin/exams/${res.data.data.id}/questions`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create exam');
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl animate-fade-in">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/admin/exams')} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
          <FiArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-black text-gray-900">Create Exam</h1>
          <p className="text-gray-500 text-sm">Configure exam settings and questions</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Basic info */}
        <div className="card p-6 space-y-4">
          <h2 className="font-bold text-gray-900 text-base border-b pb-3">Basic Information</h2>
          <Field label="Exam Title *" error={errors.title}>
            <input className="input" placeholder="e.g. Senior Software Engineer Assessment" value={form.title} onChange={e => set('title', e.target.value)} />
          </Field>
          <Field label="Description" >
            <textarea className="input resize-none" rows={3} placeholder="Brief overview of this exam..." value={form.description} onChange={e => set('description', e.target.value)} />
          </Field>
          <Field label="Instructions" hint="Shown to candidates before they start">
            <textarea className="input resize-none" rows={3} placeholder="e.g. Read each question carefully. No negative marking..." value={form.instructions} onChange={e => set('instructions', e.target.value)} />
          </Field>
          <Field label="Category">
            <select className="input" value={form.category} onChange={e => set('category', e.target.value)}>
              <option value="">Select category</option>
              {['Technical','Aptitude','Behavioral','Domain Knowledge','Language','Mixed'].map(c => <option key={c}>{c}</option>)}
            </select>
          </Field>
        </div>

        {/* Exam config */}
        <div className="card p-6 space-y-4">
          <h2 className="font-bold text-gray-900 text-base border-b pb-3">Exam Configuration</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Duration (minutes) *" error={errors.duration_minutes}>
              <input type="number" className="input" min={1} value={form.duration_minutes} onChange={e => set('duration_minutes', parseInt(e.target.value) || 0)} />
            </Field>
            <Field label="Total Questions *" error={errors.total_questions}>
              <input type="number" className="input" min={1} value={form.total_questions} onChange={e => set('total_questions', parseInt(e.target.value) || 0)} />
            </Field>
            <Field label="Passing Marks (%)" error={errors.passing_marks}>
              <input type="number" className="input" min={0} max={100} value={form.passing_marks} onChange={e => set('passing_marks', parseFloat(e.target.value) || 0)} />
            </Field>
            <Field label="Max Marks">
              <input type="number" className="input" min={1} value={form.max_marks} onChange={e => set('max_marks', parseFloat(e.target.value) || 0)} />
            </Field>
            <Field label="Marks Per Question">
              <input type="number" className="input" min={0} step={0.5} value={form.marks_per_question} onChange={e => set('marks_per_question', parseFloat(e.target.value) || 0)} />
            </Field>
            <Field label="Negative Marks" hint="Marks deducted per wrong answer">
              <input type="number" className="input" min={0} step={0.25} value={form.negative_marks} onChange={e => set('negative_marks', parseFloat(e.target.value) || 0)} />
            </Field>
            <Field label="Max Attempts">
              <input type="number" className="input" min={1} max={5} value={form.max_attempts} onChange={e => set('max_attempts', parseInt(e.target.value) || 1)} />
            </Field>
            <Field label="Display Mode">
              <select className="input" value={form.display_mode} onChange={e => set('display_mode', e.target.value)}>
                <option value="one_per_page">One Question Per Page</option>
                <option value="all">All Questions at Once</option>
              </select>
            </Field>
          </div>
        </div>

        {/* Scheduling */}
        <div className="card p-6 space-y-4">
          <h2 className="font-bold text-gray-900 text-base border-b pb-3">Scheduling (Optional)</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Start Time" hint="Leave blank for open access">
              <input type="datetime-local" className="input" value={form.start_time} onChange={e => set('start_time', e.target.value)} />
            </Field>
            <Field label="End Time">
              <input type="datetime-local" className="input" value={form.end_time} onChange={e => set('end_time', e.target.value)} />
            </Field>
          </div>
        </div>

        {/* Options */}
        <div className="card p-6">
          <h2 className="font-bold text-gray-900 text-base border-b pb-3 mb-3">Options & Security</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
            <Toggle label="Randomize Questions" desc="Shuffle question order per candidate" checked={form.is_randomized} onChange={v => set('is_randomized', v)} />
            <Toggle label="Randomize Options" desc="Shuffle answer choices" checked={form.randomize_options} onChange={v => set('randomize_options', v)} />
            <Toggle label="Allow Review" desc="Candidates can mark questions for review" checked={form.allow_review} onChange={v => set('allow_review', v)} />
            <Toggle label="Show Results Immediately" desc="Display score right after submission" checked={form.show_result_immediately} onChange={v => set('show_result_immediately', v)} />
            <Toggle label="Enable Proctoring" desc="Track tab switches & violations" checked={form.proctoring_enabled} onChange={v => set('proctoring_enabled', v)} />
          </div>
        </div>

        {/* Submit */}
        <div className="flex gap-3 pb-6">
          <button type="button" onClick={() => navigate('/admin/exams')} className="btn-secondary flex-1">Cancel</button>
          <button type="submit" disabled={loading} className="btn-primary flex-1">
            {loading ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <FiSave size={16} />}
            {loading ? 'Creating...' : 'Create & Add Questions'}
          </button>
        </div>
      </form>
    </div>
  );
}
